import React, { useState, useEffect, useCallback } from "react";
import { formatRocDate } from "../../utils/rocDate";
import ReportExportView from "./ReportExportView";
import { exportPayrollRange, loadPayrollPeople } from "../../utils/reportData";
import { buildPayrollCsv, downloadReport, monthKey } from "../../utils/reportExports";
import { collection, query, onSnapshot, doc, getDoc, setDoc, where, updateDoc, getDocs, deleteDoc, orderBy, deleteField } from "../../config/dataClient";
import { DollarSign, CheckCircle, Download, ChevronLeft, ChevronRight, Edit3, Save, X, Plus, Trash2, RefreshCw, PlayCircle, Lock, Unlock, CalendarClock, Info, ChevronDown, ChevronUp } from "lucide-react";
import { db } from "../../config/firebase";
import { APP_ID, ROLES } from "../../config/constants";
import { useToast } from "../ui/ToastConfirm";
import { getDaysInMonth, calculateTenure, formatDate } from "../../utils/timeHelpers";
import {
  calculatePayrollStats,
  calculateSalary,
  getAnniversaryInfo,
  getNextMonthAnniversaryHint,
  getPriorPriorYearRange,
  computePartTimeAnnualLeavePayout,
  calculatePartTimeWorkedRanges,
  applyAdjustments,
} from "../../utils/salaryHelpers";

// --- 子元件：預先登記加減項 Modal ---
// 讓管理員在薪資單尚未產生前，先登記加項／減項；結算時自動併入。
const AdjustmentModal = ({ user, year, month, onClose, onSaved, showToast }) => {
    const docId = `${year}_${month + 1}_${user.uid}`;
    const [plusItems, setPlusItems] = useState([]);
    const [minusItems, setMinusItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const snap = await getDoc(doc(db, "artifacts", APP_ID, "public", "data", "payroll_adjustments", docId));
                if (snap.exists()) {
                    const d = snap.data();
                    setPlusItems(d.plusItems || []);
                    setMinusItems(d.minusItems || []);
                }
            } catch (e) { console.error(e); }
            setLoading(false);
        })();
    }, [docId]);

    const edit = (type, idx, field, val) => {
        const setter = type === "plus" ? setPlusItems : setMinusItems;
        const list = type === "plus" ? plusItems : minusItems;
        const next = [...list];
        next[idx] = { ...next[idx], [field]: field === "amount" ? val.replace(/[^0-9]/g, "") : val };
        setter(next);
    };
    const add = (type) => {
        if (type === "plus") setPlusItems([...plusItems, { name: "", amount: "" }]);
        else setMinusItems([...minusItems, { name: "", amount: "" }]);
    };
    const remove = (type, idx) => {
        if (type === "plus") setPlusItems(plusItems.filter((_, i) => i !== idx));
        else setMinusItems(minusItems.filter((_, i) => i !== idx));
    };

    const save = async () => {
        setSaving(true);
        try {
            const norm = (arr) => arr
                .filter(i => String(i.name || "").trim() && Number(i.amount))
                .map(i => ({ name: String(i.name).trim(), amount: Math.round(Number(i.amount)) }));
            const payload = {
                uid: user.uid, name: user.name || "", year, month: month + 1,
                plusItems: norm(plusItems), minusItems: norm(minusItems),
                updatedAt: new Date().toISOString(),
            };
            const ref = doc(db, "artifacts", APP_ID, "public", "data", "payroll_adjustments", docId);
            if (!payload.plusItems.length && !payload.minusItems.length) await deleteDoc(ref).catch(() => {});
            else await setDoc(ref, payload);
            showToast?.("已儲存，結算時會自動併入");
            onSaved?.(user.uid, payload.plusItems.length + payload.minusItems.length);
            onClose();
        } catch (e) { alert("儲存失敗：" + e.message); }
        finally { setSaving(false); }
    };

    const row = (type, list) => (
        <div className="space-y-2">
            {list.map((it, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                    <input className="flex-1 border rounded p-1.5 text-xs" placeholder={type === "plus" ? "例：六月勞保溢扣補發" : "例：預支薪資"}
                        value={it.name || ""} onChange={e => edit(type, idx, "name", e.target.value)} />
                    <input className="w-24 border rounded p-1.5 text-xs text-right" placeholder="金額" inputMode="numeric"
                        value={it.amount ?? ""} onChange={e => edit(type, idx, "amount", e.target.value)} />
                    <button onClick={() => remove(type, idx)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
            ))}
            <button onClick={() => add(type)} className={`text-xs flex items-center gap-1 hover:underline ${type === "plus" ? "text-emerald-600" : "text-red-500"}`}>
                <Plus size={12} /> 新增{type === "plus" ? "加項" : "減項"}
            </button>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-5 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-1">
                    <h3 className="font-bold text-slate-800">預先登記加減項</h3>
                    <button onClick={onClose}><X size={18} className="text-slate-400" /></button>
                </div>
                <div className="text-xs text-slate-500 mb-4">
                    {user.name}・{year} 年 {month + 1} 月<br />
                    <span className="text-amber-600">薪資單於次月 1 號產生時，會自動併入以下項目。</span>
                </div>
                {loading ? <div className="text-center text-slate-400 py-6 text-sm">載入中...</div> : (
                    <div className="space-y-4">
                        <div>
                            <div className="text-xs font-bold text-emerald-700 mb-2">加項（增加實領）</div>
                            {row("plus", plusItems)}
                        </div>
                        <div className="border-t pt-4">
                            <div className="text-xs font-bold text-red-600 mb-2">減項（減少實領）</div>
                            {row("minus", minusItems)}
                        </div>
                    </div>
                )}
                <div className="flex gap-2 mt-5 pt-3 border-t">
                    <button onClick={onClose} className="flex-1 bg-slate-100 text-slate-600 py-2 rounded-lg text-sm font-bold hover:bg-slate-200">取消</button>
                    <button onClick={save} disabled={saving || loading} className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:bg-slate-300">
                        {saving ? "儲存中..." : "儲存"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- 子元件：單張薪資卡片 (負責 UI 顯示) ---
const PayrollSlipCard = ({ 
    slip, isAdmin, isEditing, isProcessing, editFormData, 
    onRecalculate, onStartEdit, onCancelEdit, onSaveEdit, 
    onConfirm, onCancelConfirmation, onChange, onAddItem, onRemoveItem, onItemChange 
}) => {
    const data = isEditing ? editFormData : slip;
    const isFullTime = data.role === ROLES.FULLTIME;
    const isConfirmed = data.status === 'confirmed';
    const salary = data.salaryData || {};
    const ded = salary.deductions || {};
    const [showRules, setShowRules] = useState(false);

    // 格式化確認時間
    const formatConfirmTime = (isoString) => {
        if (!isoString) return "";
        const date = new Date(isoString);
        return date.toLocaleString('zh-TW', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    return (
        <div className={`paper-item salary-slip bg-white rounded-xl shadow-sm border overflow-hidden animate-fade-in relative ${isEditing ? 'border-emerald-400 ring-2 ring-emerald-100' : 'border-slate-200'}`}>
            {isProcessing && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                </div>
            )}

            {/* 標題區 */}
            <div className={`bg-gradient-to-r ${isConfirmed ? 'from-slate-600 to-slate-700' : 'from-emerald-700 to-teal-700'} text-white p-3 flex justify-between items-center`}>
                <span className="font-bold text-lg flex items-center gap-2">
                    {data.name} 
                    <span className="text-[10px] opacity-60 font-mono bg-black/20 px-1 rounded">
                        {data.uid ? data.uid.slice(-4) : 'NULL'}
                    </span>
                    <span className="text-sm opacity-80">{data.year}年{data.month}月</span>
                    {isConfirmed && <Lock size={16} className="opacity-75"/>}
                </span>
                <div className="flex gap-2 items-center">
                    {/* 管理員按鈕 */}
                    {isAdmin && !isEditing && !isConfirmed && (
                        <>
                            <button onClick={() => onRecalculate(slip)} className="bg-white/20 p-1.5 rounded hover:bg-white/30 backdrop-blur-sm transition-colors" title="重新計算"><RefreshCw size={14}/></button>
                            <button onClick={() => onStartEdit(slip)} className="bg-white/20 p-1.5 rounded hover:bg-white/30 backdrop-blur-sm transition-colors" title="編輯"><Edit3 size={14}/></button>
                        </>
                    )}
                    {isAdmin && !isEditing && isConfirmed && (
                        <button onClick={() => onCancelConfirmation(slip)} className="bg-red-500/30 p-1.5 rounded hover:bg-red-500/50 backdrop-blur-sm transition-colors text-red-100 hover:text-white" title="取消確認 (解鎖)">
                            <Unlock size={14}/>
                        </button>
                    )}

                    {isEditing && (
                        <div className="flex gap-1">
                            <button onClick={onSaveEdit} className="bg-emerald-500 p-1.5 rounded hover:bg-emerald-400 text-white"><Save size={14}/></button>
                            <button onClick={onCancelEdit} className="bg-red-500 p-1.5 rounded hover:bg-red-400 text-white"><X size={14}/></button>
                        </div>
                    )}
                    
                    {/* 狀態標籤 & 時間 */}
                    {!isEditing && (
                        <div className="flex flex-col items-end">
                            <span className="payroll-status-badge text-xs px-2 py-1 rounded font-bold" data-status={isConfirmed ? 'confirmed' : 'pending'}>
                                {isConfirmed ? '已確認' : '未確認'}
                            </span>
                            {isConfirmed && data.confirmedAt && (
                                <span className="text-[9px] text-slate-300 font-mono mt-0.5 opacity-80">
                                    {formatConfirmTime(data.confirmedAt)}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 個人資料 */}
            <div className="p-4 bg-slate-50 border-b border-slate-100">
                <div className="grid grid-cols-2 gap-y-2 text-sm text-slate-600">
                    <div><span className="text-slate-400 text-xs block">姓名</span><span className="font-bold text-slate-800">{data.name}</span></div>
                    <div><span className="text-slate-400 text-xs block">職稱</span>{isFullTime ? '正職' : '工讀生'}</div>
                    <div><span className="text-slate-400 text-xs block">到職日</span>{data.startDate || '--'}</div>
                    <div><span className="text-slate-400 text-xs block">生日（民國）</span>{formatRocDate(data.birthday) || '--'}</div>
                    <div className="col-span-2 border-t border-slate-200 pt-2 mt-1">
                        <span className="text-slate-400 text-xs block">年資</span>
                        <span className="font-bold text-emerald-600">{calculateTenure(data.startDate)}</span>
                    </div>
                </div>
            </div>

            {/* FT 下月滿週年/半年提示（顯示在薪資單內） */}
            {isFullTime && (() => {
                const ftHint = getNextMonthAnniversaryHint(data.startDate, data.year, data.month - 1);
                if (!ftHint) return null;
                const stageText = ftHint.type === 'half_year'
                    ? '滿 6 個月'
                    : `滿 ${ftHint.yearsWillComplete} 年`;
                return (
                    <div className="mx-4 mt-3 mb-1 p-2.5 bg-blue-50 border-l-4 border-blue-400 rounded text-xs text-blue-800 flex items-start gap-2">
                        <Info size={12} className="mt-0.5 shrink-0"/>
                        <span className="leading-relaxed">
                            下個月（{ftHint.nextYear}/{String(ftHint.nextMonth).padStart(2,'0')}）{stageText}，
                            將新增 <b className="mx-1">{ftHint.daysToAdd}</b>天特休
                        </span>
                    </div>
                );
            })()}

            <div className="p-4 space-y-4">
                {/* 總薪資區塊 */}
                <div>
                    <div className="flex justify-between items-center mb-2 border-b border-emerald-100 pb-1">
                        <h4 className="text-xs font-bold text-emerald-600">應發薪資 (Gross)</h4>
                        {isEditing && <button onClick={() => onAddItem('plusItems')} className="text-xs text-emerald-500 flex items-center gap-1 hover:underline"><Plus size={10}/> 新增加項</button>}
                    </div>
                    <div className="space-y-1 text-sm text-slate-700">
                        {isFullTime ? (
                            <div className="flex justify-between items-center">
                                <span>
                                    底薪
                                    {salary.baseProrate && (
                                        <span className="ml-1 text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded px-1 py-0.5 align-middle">
                                            在職 {salary.baseProrate.days}／{salary.baseProrate.daysInMonth} 天（全月 {salary.baseProrate.fullAmount}）
                                        </span>
                                    )}
                                </span>
                                {isEditing ? <input type="number" className="w-20 border rounded p-1 text-right" value={salary.base} onChange={e=>onChange("salaryData.base", parseInt(e.target.value))}/> : <span className="font-mono">{salary.base}</span>}
                            </div>
                        ) : (
                            salary.details?.map((d, i) => (
                                <div key={i} className="flex justify-between"><span>{d.label}</span><span className="font-mono">{d.val}</span></div>
                            ))
                        )}
                        {salary.plusItems?.map((item, idx) => (
                            <div key={`plus-${idx}`}>
                                <div className="flex justify-between items-center text-emerald-600">
                                    {isEditing ? (
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => onRemoveItem('plusItems', idx)} className="text-red-400 hover:text-red-600"><Trash2 size={12}/></button>
                                            <input className="w-20 border rounded p-1 text-xs" value={item.name} onChange={e=>onItemChange('plusItems', idx, 'name', e.target.value)}/>
                                        </div>
                                    ) : <span>{item.name}</span>}
                                    {isEditing ? <input type="number" className="w-20 border rounded p-1 text-right text-xs" value={item.amount} onChange={e=>onItemChange('plusItems', idx, 'amount', e.target.value)}/> : <span className="font-mono">+{item.amount}</span>}
                                </div>
                                {!isEditing && item.meta?.type === 'pt_payout' && (
                                    <div className="text-[10px] text-slate-500 bg-emerald-50/50 rounded px-2 py-1 mt-1 ml-1 leading-relaxed border-l-2 border-emerald-200">
                                        <div>
                                            <b>計算公式</b>：
                                            ({item.meta.totalHours}h ÷ 2088) × {item.meta.days}天 × 8h × {item.meta.avgHourlyRate}/h
                                            ＝ <b className="text-emerald-700">{item.amount.toLocaleString()}</b>
                                        </div>
                                        <div className="opacity-75">
                                            上上年區間：{item.meta.periodStart} ~ {item.meta.periodEnd}
                                            ・平均時薪：{item.meta.avgHourlyRate}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="flex justify-between font-bold pt-1 border-t border-dashed border-slate-200"><span>薪資小計</span><span>{salary.gross}</span></div>
                    </div>
                </div>

                {/* 扣除額區塊 */}
                <div>
                    <div className="flex justify-between items-center mb-2 border-b border-red-100 pb-1">
                        <h4 className="text-xs font-bold text-red-500">扣除項目 (Deductions)</h4>
                        {isEditing && <button onClick={() => onAddItem('minusItems')} className="text-xs text-red-500 flex items-center gap-1 hover:underline"><Plus size={10}/> 新增扣項</button>}
                    </div>
                    <div className="space-y-1 text-sm text-slate-700">
                        <div className="flex justify-between items-center">
                            <span>
                                勞保費
                                {ded.laborProrate && (
                                    <span className="ml-1 text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded px-1 py-0.5 align-middle">
                                        加保 {ded.laborProrate.days} 天／30（全額 {ded.laborProrate.fullAmount}）
                                    </span>
                                )}
                            </span>
                            {isEditing ? <input type="number" className="w-20 border rounded p-1 text-right" value={ded.labor} onChange={e=>onChange("salaryData.deductions.labor", parseInt(e.target.value))}/> : <span className="font-mono text-red-500">-{ded.labor}</span>}
                        </div>
                        <div className="flex justify-between items-center">
                            <span>
                                健保費
                                {ded.healthWaived && (
                                    <span className="ml-1 text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded px-1 py-0.5 align-middle">
                                        離職當月免收
                                    </span>
                                )}
                            </span>
                            {isEditing ? <input type="number" className="w-20 border rounded p-1 text-right" value={ded.health} onChange={e=>onChange("salaryData.deductions.health", parseInt(e.target.value))}/> : <span className="font-mono text-red-500">-{ded.health}</span>}
                        </div>
                        {isFullTime && ded.leaveDetails?.map((ld, idx) => (
                            <div key={idx} className="flex justify-between items-center">
                                <span>{ld.name} ({ld.days}天)</span>
                                {isEditing ? <input type="number" className="w-20 border rounded p-1 text-right" defaultValue={ld.amount} onChange={e => {const newDetails = [...ded.leaveDetails];newDetails[idx].amount = parseInt(e.target.value);onChange("salaryData.deductions.leaveDetails", newDetails);}}/> : <span className="font-mono text-red-500">-{ld.amount}</span>}
                            </div>
                        ))}
                        {(ded.late > 0 || (salary.lateCount || 0) > 0) && (
                            <div className="flex justify-between items-center">
                                <span>遲到 (共{salary.lateMinutes || 0}分鐘)</span>
                                {isEditing ? <input type="number" className="w-20 border rounded p-1 text-right" value={ded.late} onChange={e=>onChange("salaryData.deductions.late", parseInt(e.target.value))}/> : <span className="font-mono text-red-500">-{ded.late}</span>}
                            </div>
                        )}
                        {salary.minusItems?.map((item, idx) => (
                            <div key={`minus-${idx}`} className="flex justify-between items-center text-red-500">
                                {isEditing ? (
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => onRemoveItem('minusItems', idx)} className="text-red-400 hover:text-red-600"><Trash2 size={12}/></button>
                                        <input className="w-20 border rounded p-1 text-xs" value={item.name} onChange={e=>onItemChange('minusItems', idx, 'name', e.target.value)}/>
                                    </div>
                                ) : <span>{item.name}</span>}
                                {isEditing ? <input type="number" className="w-20 border rounded p-1 text-right text-xs" value={item.amount} onChange={e=>onItemChange('minusItems', idx, 'amount', e.target.value)}/> : <span className="font-mono">-{item.amount}</span>}
                            </div>
                        ))}
                        <div className="flex justify-between font-bold pt-1 border-t border-dashed border-slate-200 text-red-600"><span>扣除總額</span><span>-{ded.total}</span></div>
                    </div>
                </div>

                <div className={`p-4 rounded-xl flex justify-between items-center border ${isConfirmed ? 'bg-slate-100 border-slate-200' : 'bg-emerald-50 border-emerald-100'}`}>
                    <span className="font-bold text-slate-700">本月應收薪資</span>
                    <span className={`font-black text-2xl font-mono ${isConfirmed ? 'text-slate-600' : 'text-emerald-700'}`}>${salary.net}</span>
                </div>

                {(!isConfirmed && !isEditing) && (
                    <button onClick={() => onConfirm(slip)} className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors flex justify-center items-center gap-2 shadow-lg shadow-emerald-200">
                        <CheckCircle size={18}/> {isAdmin ? '確認並鎖定薪資單' : '確認薪資無誤'}
                    </button>
                )}

                {/* 薪資說明 (僅正職) */}
                {isFullTime && (
                    <div className="border-t border-slate-100 pt-2">
                        <button onClick={() => setShowRules(!showRules)} className="w-full flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-slate-600 py-1 transition-colors">
                            <Info size={12} /> 薪資計算說明 {showRules ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                        {showRules && (
                            <div className="mt-2 bg-slate-50 p-3 rounded-lg text-[10px] text-slate-500 leading-relaxed space-y-1 animate-fade-in border border-slate-100">
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>遲到扣薪：依遲到分鐘數按比例扣除（底薪 ÷ 當月天數 ÷ 7.5小時 ÷ 60分鐘 × 遲到分鐘數）</li>
                                    <li>如不足月換算日薪計，皆以當月天數計算</li>
                                    <li>如不足日換算時薪計，皆以7.5小時計算(時薪四捨五入到整位數)</li>
                                    <li>請假休假事宜，依照勞工基準法為主，假單須提前呈遞、病假後上班當日補呈簽</li>
                                    <li>請假(無論事假病假)一天以內以時數扣除計算，一天以上照勞工基準法</li>
                                    <li>請假薪資：事假扣除單日日薪(底薪/當月天數計算)、病假半日薪、生理假半日薪</li>
                                    <li>生理假依性別工作平等法第14條</li>
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// --- 主元件：PayrollView (負責資料邏輯) ---
export default function PayrollView({ user, userProfile, role }) {
  const [currentDate, setCurrentDate] = useState(() => {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  }); 
  const [payrollData, setPayrollData] = useState([]);
  const [showRangeExport, setShowRangeExport] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  
  const [isLockedByDate, setIsLockedByDate] = useState(false);
  const [unlockDateObj, setUnlockDateObj] = useState(null);

  const [processingId, setProcessingId] = useState(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // 年資相關提示（產生薪資單時收集）
  const [anniversaryHints, setAnniversaryHints] = useState([]);

  const [batchProgress, setBatchProgress] = useState(""); 

  const [editingSlipId, setEditingSlipId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // 預先登記加減項
  const [adjTarget, setAdjTarget] = useState(null);
  const [adjCounts, setAdjCounts] = useState({});
  const showToast = useToast();

  const isAdmin = role === ROLES.ADMIN;
  const loadReportUsers = useCallback(() => loadPayrollPeople(isAdmin ? ROLES.ADMIN : null), [isAdmin]);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth(); 
  
  useEffect(() => {
    if (!isAdmin) {
        setAllUsers([userProfile ? { ...userProfile, uid: user.uid } : null].filter(Boolean));
        return;
    }
    const q = query(collection(db, "artifacts", APP_ID, "public", "data", "daily_status"));
    const unsub = onSnapshot(q, async (snapshot) => {
       const users = [];
       for (const d of snapshot.docs) {
          const uid = d.id; 
          const profileSnap = await getDoc(doc(db, "artifacts", APP_ID, "users", uid, "profile", "main"));
          const pData = profileSnap.exists() ? profileSnap.data() : {};
          if (pData.role !== ROLES.ADMIN) {
              const displayName = pData.fullName || pData.name || d.data().name;
              users.push({ ...pData, uid: uid, name: displayName }); 
          }
       }
       setAllUsers(users);
    });
    return () => unsub();
  }, [isAdmin, user, userProfile]);

  useEffect(() => {
    if (allUsers.length === 0) return;
    loadPayrollData();
  }, [currentDate, allUsers]);

  // 載入本月已預先登記的加減項數量（供列表顯示標記）
  useEffect(() => {
    if (!isAdmin || allUsers.length === 0) return;
    let cancelled = false;
    (async () => {
      const counts = {};
      await Promise.all(allUsers.map(async (u) => {
        try {
          const snap = await getDoc(doc(db, "artifacts", APP_ID, "public", "data", "payroll_adjustments", `${year}_${month + 1}_${u.uid}`));
          if (snap.exists()) {
            const d = snap.data();
            const n = (d.plusItems?.length || 0) + (d.minusItems?.length || 0);
            if (n > 0) counts[u.uid] = n;
          }
        } catch (e) { /* 忽略單筆讀取失敗 */ }
      }));
      if (!cancelled) setAdjCounts(counts);
    })();
    return () => { cancelled = true; };
  }, [isAdmin, allUsers, year, month]);

  const loadPayrollData = async () => {
      const now = new Date();
      const unlockDate = new Date(year, month + 1, 1, 0, 0, 0);
      setUnlockDateObj(unlockDate);

      if (now < unlockDate) {
          setIsLockedByDate(true);
          setPayrollData([]);
          setAnniversaryHints([]);
          return;
      }

      setIsLockedByDate(false);

      const slips = [];
      const hints = [];
      const daysInMonthTotal = getDaysInMonth(year, month).length;

      try {
          for (const u of allUsers) {
              // [新增] 正職下月滿週年提示（不論薪資單是否已存在）
              if (u.role === ROLES.FULLTIME && !u.isHidden) {
                  const ftHint = getNextMonthAnniversaryHint(u.startDate, year, month);
                  if (ftHint) {
                      hints.push({ ...ftHint, subType: ftHint.type, type: 'ft_next_anniversary', name: u.name });
                  }
              }

              const slipId = `${year}_${month + 1}_${u.uid}`;
              const slipRef = doc(db, "artifacts", APP_ID, "public", "data", "payroll_slips", slipId);
              const slipSnap = await getDoc(slipRef);

              if (slipSnap.exists()) {
                  const existingData = slipSnap.data();
                  // 舊薪資單（有 bonus 欄位或缺少 lateMinutes）用新公式完整重新計算
                  const needsMigration = existingData.salaryData && ('bonus' in existingData.salaryData || existingData.salaryData.lateMinutes == null);
                  if (needsMigration && existingData.status !== 'confirmed') {
                      try {
                          const result = await generateSlip(u, year, month, daysInMonthTotal);
                          if (result.slip) {
                              // 保留手動加減項，但若新計算有特休換算（系統自動加項），優先使用新的
                              const oldPlus = (existingData.salaryData.plusItems || []).filter(p => !String(p.name || '').startsWith('特休換算'));
                              const newAutoPlus = (result.slip.salaryData.plusItems || []).filter(p => String(p.name || '').startsWith('特休換算'));
                              result.slip.salaryData.plusItems = [...oldPlus, ...newAutoPlus];
                              result.slip.salaryData.minusItems = existingData.salaryData.minusItems || [];
                              const updated = { ...existingData, salaryData: result.slip.salaryData };
                              await setDoc(slipRef, updated);
                              slips.push({ id: slipId, ...updated, name: u.name, uid: u.uid });
                              if (result.hint) hints.push(result.hint);
                          } else {
                              slips.push({ id: slipId, ...existingData, name: u.name, uid: u.uid });
                          }
                      } catch (e) {
                          console.warn(`重算 ${u.name} 薪資單失敗:`, e);
                          slips.push({ id: slipId, ...existingData, name: u.name, uid: u.uid });
                      }
                  } else {
                      slips.push({ id: slipId, ...existingData, name: u.name, uid: u.uid });
                  }
              } else {
                  try {
                      const result = await generateSlip(u, year, month, daysInMonthTotal);
                      if (result.slip) {
                          await setDoc(slipRef, result.slip);
                          slips.push({ id: slipId, ...result.slip });
                          if (result.hint) hints.push(result.hint);
                      }
                  } catch (err) {
                      console.error(`生成 ${u.name} 薪資單失敗:`, err);
                  }
              }
          }
          setPayrollData(slips);
          setAnniversaryHints(hints);
      } catch (error) {
          console.error("載入錯誤:", error);
      }
  };

  const generateSlip = async (targetUser, y, m, daysCount) => {
      let logs = [];
      let leaves = [];

      try {
          const startDt = new Date(y, m, 1, 0, 0, 0);
          const endDt = new Date(y, m + 1, 0, 23, 59, 59, 999);

          const logsRef = collection(db, "artifacts", APP_ID, "users", targetUser.uid, "logs");
          const qLogs = query(logsRef, where("timestamp", ">=", startDt), where("timestamp", "<=", endDt), orderBy("timestamp", "asc"));
          const logsSnap = await getDocs(qLogs);
          logs = logsSnap.docs.map(d => ({ ...d.data(), dateObj: d.data().timestamp.toDate(), isIgnored: false }));

          const leavesRef = collection(db, "artifacts", APP_ID, "public", "data", "leaves");
          const leavesSnap = await getDocs(leavesRef);
          leaves = leavesSnap.docs.map(d => d.data()).filter(l => l.uid === targetUser.uid && l.status === 'approved');

      } catch (e) { console.warn("Data fetch failed", e); }

      const stats = calculatePayrollStats(targetUser, logs, leaves, y, m);
      if (!stats.hasLogs) return { slip: null, hint: null };

      let salaryData = calculateSalary(targetUser, stats, daysCount, y, m);
      if (!salaryData) return { slip: null, hint: null };

      // ===== 工讀生：本月若為入職週年月，加上「特休換算薪資」=====
      // 隱藏的員工不顯示提示
      let hint = null;
      if (targetUser.role !== ROLES.FULLTIME && targetUser.startDate && !targetUser.isHidden) {
          const anniv = getAnniversaryInfo(targetUser.startDate, y, m);
          if (anniv.isAnniversary) {
              const range = getPriorPriorYearRange(targetUser.startDate, anniv.yearsCompleted);
              if (!range) {
                  // 年資未滿 2 年 → 無上上年可換算
                  hint = { type: 'pt_no_data', name: targetUser.name, yearsCompleted: anniv.yearsCompleted, reason: '年資未滿 2 年' };
              } else {
                  try {
                      // 1. 抓上上年區間的打卡紀錄
                      const logsRef = collection(db, "artifacts", APP_ID, "users", targetUser.uid, "logs");
                      const qPrior = query(logsRef, where("timestamp", ">=", range.start), where("timestamp", "<", range.end));
                      const priorSnap = await getDocs(qPrior);
                      const priorLogs = priorSnap.docs.map(d => ({ ...d.data(), dateObj: d.data().timestamp.toDate(), isIgnored: false }));

                      // 2. 按日 group，使用工時規則 A 計算總工時
                      const logsByDay = {};
                      priorLogs.forEach(l => {
                          const key = formatDate(l.dateObj);
                          if (!logsByDay[key]) logsByDay[key] = [];
                          logsByDay[key].push(l);
                      });
                      let totalHours = 0;
                      for (const k of Object.keys(logsByDay)) {
                          totalHours += calculatePartTimeWorkedRanges(logsByDay[k]).hours;
                      }

                      // 3. 抓上上年 12 個月薪資單，加總實領金額
                      let totalWage = 0;
                      let slipsFound = 0;
                      const cursor = new Date(range.start);
                      while (cursor < range.end) {
                          const slipId = `${cursor.getFullYear()}_${cursor.getMonth() + 1}_${targetUser.uid}`;
                          const ref = doc(db, "artifacts", APP_ID, "public", "data", "payroll_slips", slipId);
                          const snap = await getDoc(ref);
                          if (snap.exists()) {
                              const sd = snap.data().salaryData || {};
                              totalWage += Number(sd.gross || sd.base || 0);
                              slipsFound++;
                          }
                          cursor.setMonth(cursor.getMonth() + 1);
                      }
                      // 若無歷史薪資單 → 用目前時薪估算
                      if (slipsFound === 0) {
                          totalWage = totalHours * (Number(targetUser.hourlyRate) || 0);
                      }

                      if (totalHours <= 0 || totalWage <= 0) {
                          hint = { type: 'pt_no_data', name: targetUser.name, yearsCompleted: anniv.yearsCompleted, reason: '上上年無打卡或薪資紀錄' };
                      } else {
                          const payout = computePartTimeAnnualLeavePayout({
                              yearsCompleted: anniv.yearsCompleted,
                              totalHours,
                              totalWage,
                          });
                          if (!payout) {
                              hint = { type: 'pt_no_data', name: targetUser.name, yearsCompleted: anniv.yearsCompleted, reason: '無對應特休天數' };
                          } else {
                              // 加入 plusItems（同名項目先移除避免重複）
                              const existingPlus = (salaryData.plusItems || []).filter(p => !String(p.name || '').startsWith('特休換算'));
                              const itemName = `特休換算（滿${anniv.yearsCompleted}年・${payout.days}天）`;
                              const hoursR = Math.round(totalHours * 10) / 10;
                              salaryData.plusItems = [...existingPlus, {
                                  name: itemName,
                                  amount: payout.amount,
                                  meta: {
                                      type: 'pt_payout',
                                      totalHours: hoursR,
                                      avgHourlyRate: payout.avgHourlyRate,
                                      days: payout.days,
                                      yearsCompleted: anniv.yearsCompleted,
                                      periodStart: formatDate(range.start),
                                      periodEnd: formatDate(new Date(range.end.getTime() - 86400000)),
                                  }
                              }];
                              // 重算 net
                              const plusTotal = salaryData.plusItems.reduce((a, p) => a + Number(p.amount || 0), 0);
                              const minusTotal = (salaryData.minusItems || []).reduce((a, p) => a + Number(p.amount || 0), 0);
                              salaryData.net = (salaryData.gross || 0) + plusTotal - minusTotal - (salaryData.deductions?.total || 0);
                              hint = {
                                  type: 'pt_payout',
                                  name: targetUser.name,
                                  amount: payout.amount,
                                  days: payout.days,
                                  yearsCompleted: anniv.yearsCompleted,
                                  totalHours: Math.round(totalHours * 10) / 10,
                                  avgHourlyRate: payout.avgHourlyRate,
                                  periodStart: formatDate(range.start),
                                  periodEnd: formatDate(new Date(range.end.getTime() - 86400000)),
                              };
                          }
                      }
                  } catch (e) {
                      console.warn(`特休換算讀取上上年資料失敗 (${targetUser.name}):`, e);
                      hint = { type: 'pt_no_data', name: targetUser.name, yearsCompleted: anniv.yearsCompleted, reason: '讀取上上年資料失敗' };
                  }
              }
          }
      }

      // 併入管理員預先登記的加減項
      try {
          const adjSnap = await getDoc(doc(db, "artifacts", APP_ID, "public", "data", "payroll_adjustments", `${y}_${m + 1}_${targetUser.uid}`));
          if (adjSnap.exists()) applyAdjustments(salaryData, adjSnap.data());
      } catch (e) { console.warn("讀取預先登記加減項失敗", e); }

      return {
          slip: {
              uid: targetUser.uid,
              name: targetUser.name || "未命名",
              role: targetUser.role || ROLES.PARTTIME,
              startDate: targetUser.startDate || "",
              birthday: targetUser.birthday || "",
              year: y,
              month: m + 1,
              status: 'draft',
              salaryData: salaryData
          },
          hint,
      };
  };
  
  const handleRecalculate = async (slip) => {
      if (slip.status === 'confirmed') return alert("此薪資單已確認，無法重新計算。");
      if(!confirm(`確定要重新計算 ${slip.name} 的薪資單嗎？`)) return;

      setProcessingId(slip.id);
      try {
          const u = allUsers.find(u => u.uid === slip.uid);
          if(!u) return;

          const daysInMonthTotal = getDaysInMonth(year, month).length;
          const result = await generateSlip(u, year, month, daysInMonthTotal);
          const slipRef = doc(db, "artifacts", APP_ID, "public", "data", "payroll_slips", slip.id);

          if (result.slip) {
              await setDoc(slipRef, result.slip);
              setPayrollData(prev => prev.map(p => p.id === slip.id ? { id: slip.id, ...result.slip } : p));
              if (result.hint) setAnniversaryHints(prev => [...prev.filter(h => h.name !== result.hint.name || h.type !== result.hint.type), result.hint]);
          } else {
              await deleteDoc(slipRef);
              setPayrollData(prev => prev.filter(p => p.id !== slip.id));
          }
      } catch(e) { alert("重新計算失敗"); } finally { setProcessingId(null); }
  };

  const handleBatchRecalculate = async () => {
      if (!confirm(`確定要重新計算所有人的薪資嗎？\n已確認的薪資單將被跳過。`)) return;
      setIsBatchProcessing(true);
      setBatchProgress("準備開始...");
      try {
          const daysInMonthTotal = getDaysInMonth(year, month).length;
          const newSlips = [];
          const newHints = [];
          for (let i = 0; i < allUsers.length; i++) {
              const u = allUsers[i];
              // 正職下月滿週年提示
              if (u.role === ROLES.FULLTIME && !u.isHidden) {
                  const ftHint = getNextMonthAnniversaryHint(u.startDate, year, month);
                  if (ftHint) newHints.push({ ...ftHint, subType: ftHint.type, type: 'ft_next_anniversary', name: u.name });
              }

              const existingSlip = payrollData.find(p => p.uid === u.uid);
              if (existingSlip && existingSlip.status === 'confirmed') {
                  newSlips.push(existingSlip);
                  continue;
              }
              setBatchProgress(`正在計算 (${i + 1}/${allUsers.length}): ${u.name}`);
              const result = await generateSlip(u, year, month, daysInMonthTotal);
              const slipId = `${year}_${month + 1}_${u.uid}`;
              const slipRef = doc(db, "artifacts", APP_ID, "public", "data", "payroll_slips", slipId);
              if (result.slip) {
                  await setDoc(slipRef, result.slip);
                  newSlips.push({ id: slipId, ...result.slip });
                  if (result.hint) newHints.push(result.hint);
              } else if (existingSlip) {
                  await deleteDoc(slipRef);
              }
          }
          setPayrollData(newSlips);
          setAnniversaryHints(newHints);
          setBatchProgress("完成！");
          setTimeout(() => setIsBatchProcessing(false), 1000);
      } catch (e) { alert("批次計算發生錯誤"); setIsBatchProcessing(false); }
  };

  const confirmSlip = async (slip) => {
      if (!confirm("確認薪資單內容無誤？一旦確認將鎖定。")) return;
      setProcessingId(slip.id);
      try {
          const ref = doc(db, "artifacts", APP_ID, "public", "data", "payroll_slips", slip.id);
          if (isAdmin) {
             await setDoc(ref, { 
                  status: 'confirmed', 
                  confirmedAt: new Date().toISOString(),
                  uid: slip.uid 
              }, { merge: true });
          } else {
              await updateDoc(ref, { 
                  status: 'confirmed', 
                  confirmedAt: new Date().toISOString() 
              });
          }
          setPayrollData(prev => prev.map(p => p.id === slip.id ? { ...p, status: 'confirmed', confirmedAt: new Date().toISOString() } : p));
          alert("✅ 已確認完成！");
      } catch(e) { 
          console.error(e); 
          alert("❌ 確認失敗：\n" + e.message + "\n請截圖回報管理員。");
      } finally { 
          setProcessingId(null); 
      }
  };

  const handleCancelConfirmation = async (slip) => {
      if (!confirm("確定要取消此薪資單的確認狀態嗎？\n這將允許重新計算或編輯。")) return;
      setProcessingId(slip.id);
      try {
          await updateDoc(doc(db, "artifacts", APP_ID, "public", "data", "payroll_slips", slip.id), { 
              status: 'draft', 
              confirmedAt: deleteField() 
          });
          setPayrollData(prev => prev.map(p => p.id === slip.id ? { ...p, status: 'draft', confirmedAt: null } : p));
          alert("已取消確認！");
      } catch(e) {
          console.error(e);
          alert("取消失敗：" + e.message);
      } finally {
          setProcessingId(null);
      }
  };

  const startEditing = (slip) => { setEditingSlipId(slip.id); setEditFormData(JSON.parse(JSON.stringify(slip))); };
  const cancelEditing = () => { setEditingSlipId(null); setEditFormData({}); };
  const saveEditing = async () => {
      setProcessingId(editingSlipId);
      try {
          await setDoc(doc(db, "artifacts", APP_ID, "public", "data", "payroll_slips", editingSlipId), editFormData);
          setPayrollData(prev => prev.map(p => p.id === editingSlipId ? editFormData : p));
          setEditingSlipId(null);
      } catch (e) { alert("儲存失敗"); } finally { setProcessingId(null); }
  };

  const handleEditChange = (path, value) => {
      const newData = { ...editFormData };
      const keys = path.split('.');
      if (keys.length === 2) { if (!newData[keys[0]]) newData[keys[0]] = {}; newData[keys[0]][keys[1]] = value; } 
      else if (keys.length === 3) { if (!newData[keys[0]]) newData[keys[0]] = {}; if (!newData[keys[0]][keys[1]]) newData[keys[0]][keys[1]] = {}; newData[keys[0]][keys[1]][keys[2]] = value; }

      const base = parseInt(newData.salaryData.base || 0);
      const bonus = parseInt(newData.salaryData.bonus || 0);
      const plusTotal = (newData.salaryData.plusItems || []).reduce((acc, cur) => acc + parseInt(cur.amount || 0), 0);
      const gross = base + bonus + plusTotal;
      
      const labor = parseInt(newData.salaryData.deductions.labor || 0);
      const health = parseInt(newData.salaryData.deductions.health || 0);
      const late = parseInt(newData.salaryData.deductions.late || 0);
      const leave = (newData.salaryData.deductions.leaveDetails || []).reduce((acc, cur) => acc + parseInt(cur.amount||0), 0);
      const minusTotal = (newData.salaryData.minusItems || []).reduce((acc, cur) => acc + parseInt(cur.amount || 0), 0);
      const totalDeduct = labor + health + late + leave + minusTotal;
      
      newData.salaryData.gross = gross;
      newData.salaryData.deductions.total = totalDeduct;
      newData.salaryData.net = gross - totalDeduct;
      setEditFormData(newData);
  };

  const handleAddItem = (type) => { const newData = { ...editFormData }; if (!newData.salaryData[type]) newData.salaryData[type] = []; newData.salaryData[type].push({ name: "新增項目", amount: 0 }); setEditFormData(newData); };
  const handleRemoveItem = (type, index) => { const newData = { ...editFormData }; newData.salaryData[type].splice(index, 1); handleEditChange("salaryData.base", newData.salaryData.base); };
  const handleItemChange = (type, index, field, value) => { const newData = { ...editFormData }; newData.salaryData[type][index][field] = field === 'amount' ? parseInt(value) : value; handleEditChange("salaryData.base", newData.salaryData.base); };

  // [修改] 匯出報表：將實領薪資移到姓名旁邊
  const handleExport = () => {
    if (!isAdmin || isLockedByDate) return;
    try {
      const slips = payrollData.filter(p => Number(p.year) === year && Number(p.month) === month + 1);
      downloadReport({ content: buildPayrollCsv(slips), mime: "text/csv;charset=utf-8", filename: `Payroll_${year}_${month + 1}.csv` });
    } catch (error) { showToast(error.message); }
  };

  // [新增] 排序函式：未確認在前，已確認在後
  const sortSlips = (a, b) => {
      // 1. 優先比較狀態：未確認 (draft/pending) < 已確認 (confirmed)
      // 若 a 是已確認(true)，b 是未確認(false) -> a 排後面 (1)
      const isConfirmedA = a.status === 'confirmed';
      const isConfirmedB = b.status === 'confirmed';
      if (isConfirmedA !== isConfirmedB) {
          return isConfirmedA ? 1 : -1;
      }
      // 2. 如果狀態相同，依姓名排序 (可選)
      return a.name.localeCompare(b.name);
  };

  // 應用排序
  const fullTimeSlips = payrollData.filter(p => p.role === ROLES.FULLTIME).sort(sortSlips);
  const partTimeSlips = payrollData.filter(p => p.role !== ROLES.FULLTIME).sort(sortSlips);

  const renderSlipList = (list, title) => (
      <div>
          <h3 className="text-sm font-bold text-slate-500 mb-3 px-1">{title} ({list.length})</h3>
          <div className="space-y-6">
              {list.map(slip => (
                  <PayrollSlipCard 
                      key={slip.id}
                      slip={slip}
                      isAdmin={isAdmin}
                      isEditing={editingSlipId === slip.id}
                      isProcessing={processingId === slip.id}
                      editFormData={editFormData}
                      onRecalculate={handleRecalculate}
                      onStartEdit={startEditing}
                      onCancelEdit={cancelEditing}
                      onSaveEdit={saveEditing}
                      onConfirm={confirmSlip}
                      onCancelConfirmation={handleCancelConfirmation}
                      onChange={handleEditChange}
                      onAddItem={handleAddItem}
                      onRemoveItem={handleRemoveItem}
                      onItemChange={handleItemChange}
                  />
              ))}
          </div>
      </div>
  );

  if (showRangeExport && isAdmin) return <ReportExportView
    key={user.uid}
    title="匯出區間薪資明細"
    initialMonth={monthKey(currentDate)}
    loadUsers={loadReportUsers}
    onBack={() => setShowRangeExport(false)}
    onExport={options => exportPayrollRange(isAdmin ? ROLES.ADMIN : null, options)}
  />;

  return (
    <div className="paper-page payroll-view p-4 pb-24 relative">
      {adjTarget && (
        <AdjustmentModal
          user={adjTarget}
          year={year}
          month={month}
          onClose={() => setAdjTarget(null)}
          onSaved={(uid, n) => setAdjCounts(prev => { const next = { ...prev }; if (n > 0) next[uid] = n; else delete next[uid]; return next; })}
          showToast={showToast}
        />
      )}
      {isBatchProcessing && (
          <div className="fixed inset-0 bg-black/50 z-50 flex flex-col items-center justify-center text-white">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-white mb-4"></div>
              <div className="font-bold text-lg">{batchProgress}</div>
          </div>
      )}

      <div className="page-actions flex justify-between items-start gap-2 mb-4">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 shrink-0">
          <DollarSign className="w-5 h-5 text-emerald-600" /> 薪資管理
        </h2>
        {isAdmin && (
            <div className="flex flex-wrap justify-end gap-2">
                {!isLockedByDate && <button onClick={handleBatchRecalculate} className="bg-slate-700 text-white px-2 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm hover:bg-slate-600 transition-colors">
                    <PlayCircle size={14}/> 全部重新計算
                </button>}
                {!isLockedByDate && <button onClick={handleExport} className="bg-emerald-600 text-white px-2 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm hover:bg-emerald-700 transition-colors">
                    <Download size={14}/> 匯出此月報表
                </button>}
                <button onClick={() => { if (isAdmin) setShowRangeExport(true); }} className="bg-white border border-slate-200 text-slate-700 px-2 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm">
                    <Download size={14}/> 匯出區間明細
                </button>
            </div>
        )}
      </div>

      <div className="paper-month-nav bg-white p-3 rounded-xl border border-slate-100 mb-4 flex justify-between items-center shadow-sm">
         <button onClick={() => setCurrentDate(new Date(year, month - 1))} className="p-2 hover:bg-slate-50 rounded-full text-slate-500 hover:text-emerald-600 transition-colors"><ChevronLeft size={20}/></button>
         <div className="font-bold text-slate-700 text-lg">{year}年 {month + 1}月</div>
         <button onClick={() => setCurrentDate(new Date(year, month + 1))} className="p-2 hover:bg-slate-50 rounded-full text-slate-500 hover:text-emerald-600 transition-colors"><ChevronRight size={20}/></button>
      </div>

      {/* 年資相關提示 */}
      {anniversaryHints.length > 0 && (
        <div className="mb-4 space-y-2">
          {anniversaryHints.map((h, i) => {
            if (h.type === 'pt_payout') {
              return (
                <div key={i} className="p-3 rounded-lg border-l-4 border-emerald-500 bg-emerald-50 text-emerald-800 text-xs">
                  <div className="font-bold mb-1 flex items-center gap-1">
                    <Info size={12}/> {h.name} 滿 {h.yearsCompleted} 年 — 特休換算已自動加入薪資單
                  </div>
                  <div className="opacity-80 leading-relaxed">
                    換算金額 <b>NTD {h.amount.toLocaleString()}</b> ({h.days} 天)
                    ・上上年總工時 {h.totalHours} h
                    ・平均時薪 {h.avgHourlyRate}
                    ・區間 {h.periodStart} ~ {h.periodEnd}
                  </div>
                </div>
              );
            }
            if (h.type === 'pt_no_data') {
              return (
                <div key={i} className="p-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 text-amber-800 text-xs">
                  <Info size={12} className="inline mr-1"/>
                  <b>{h.name}</b> 滿 {h.yearsCompleted} 年 — 無法計算特休換算（{h.reason || '上上年無資訊'}），請管理員手動處理。
                </div>
              );
            }
            if (h.type === 'ft_next_anniversary') {
              const stageText = h.subType === 'half_year'
                ? '滿 6 個月'
                : `滿 ${h.yearsWillComplete} 年`;
              return (
                <div key={i} className="p-3 rounded-lg border-l-4 border-blue-500 bg-blue-50 text-blue-800 text-xs">
                  <Info size={12} className="inline mr-1"/>
                  <b>{h.name}</b> 下個月（{h.nextYear}/{String(h.nextMonth).padStart(2,'0')}）{stageText}，
                  將新增 <b>{h.daysToAdd}</b> 天特休。
                </div>
              );
            }
            return null;
          })}
        </div>
      )}

      {isLockedByDate ? (
          <div className="space-y-4">
              <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <CalendarClock size={40} className="mb-3 text-emerald-200"/>
                  <h3 className="text-lg font-bold text-slate-600 mb-1">尚未開放結算</h3>
                  <p className="text-sm">本月薪資將於 <span className="font-bold text-emerald-600">{unlockDateObj?.getFullYear()}年 {unlockDateObj?.getMonth() + 1}月 1號</span> 自動結算。</p>
              </div>

              {isAdmin && allUsers.length > 0 && (
                  <div className="paper-section bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                      <h4 className="font-bold text-slate-700 text-sm mb-1 flex items-center gap-2">
                          <Plus size={16} className="text-blue-500"/> 預先登記加減項
                      </h4>
                      <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                          現在就可以先登記本月的加項（如補發）或減項（如預支）。
                          <span className="text-amber-600">薪資單結算時會自動併入計算</span>，不必等薪資單出來再手動加。
                      </p>
                      <div className="space-y-2">
                          {allUsers.filter(u => !u.isHidden).map(u => (
                              <div key={u.uid} className="flex justify-between items-center bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                                  <div className="text-sm text-slate-700 font-medium flex items-center gap-2">
                                      {u.name}
                                      {adjCounts[u.uid] > 0 && (
                                          <span className="text-[10px] bg-blue-100 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
                                              已登記 {adjCounts[u.uid]} 項
                                          </span>
                                      )}
                                  </div>
                                  <button onClick={() => setAdjTarget(u)} className="text-xs bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg font-bold hover:bg-slate-100">
                                      登記
                                  </button>
                              </div>
                          ))}
                      </div>
                  </div>
              )}
          </div>
      ) : (
          <div className="space-y-8">
              {payrollData.length === 0 ? (
                  <div className="text-center text-slate-400 py-10 bg-slate-50 rounded-xl border border-slate-100 border-dashed">本月尚無薪資單資料</div>
              ) : (
                  <>
                      {fullTimeSlips.length > 0 && renderSlipList(fullTimeSlips, "正職人員")}
                      {partTimeSlips.length > 0 && renderSlipList(partTimeSlips, "工讀生")}
                  </>
              )}
          </div>
      )}
    </div>
  );
}
